/**
 * NOTE: this file is duplicated in mppi-admin, mppi-portal and mppi-partner.
 * The three SPAs are separate repos with no shared frontend package, so the
 * existing architecture duplicates rather than imports. Keep them in step: a
 * change here that is not mirrored produces two tenants of the same brand that
 * do not match, which is invisible until somebody compares two screens.
 */
/**
 * Brand colour → CSS custom properties (MPPI-00263).
 *
 * `group_branding` stores exactly TWO colours, primary and accent, because
 * that is what a non-technical operator can reasonably choose in an admin
 * form. Tailwind wants seven: each of those plus light/dark (and muted for
 * accent).
 *
 * The variants are therefore DERIVED rather than stored. Two reasons:
 *
 *  - the schema and the admin UI stay at two colours, so adding a tenant does
 *    not mean picking seven hexes that have to relate to each other correctly;
 *  - the variants cannot drift out of relation with their base, which is what
 *    happens when seven independent values are edited by hand over time.
 *
 * Lightness is shifted in HSL rather than by scaling RGB. Scaling RGB darkens
 * saturated colours unevenly — a gold and a navy scaled by the same factor do
 * not read as "one step darker" — whereas an HSL lightness shift is at least
 * perceptually consistent across hues. It is not a perceptual colour space
 * (OKLCH would be better), but it is dependency-free and good enough for a
 * one-step variant; swapping the two functions below is the whole upgrade path
 * if it ever proves not to be.
 */

/** "#0E7C7B" → [14, 124, 123]. Tolerates missing "#" and 3-digit shorthand. */
export function hexToRgb(hex: string): [number, number, number] | null {
  const raw = (hex || "").trim().replace(/^#/, "");
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rr = r / 255;
  const gg = g / 255;
  const bb = b / 255;
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rr) h = ((gg - bb) / d + (gg < bb ? 6 : 0)) / 6;
  else if (max === gg) h = ((bb - rr) / d + 2) / 6;
  else h = ((rr - gg) / d + 4) / 6;
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number): number => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  return [
    Math.round(channel(h + 1 / 3) * 255),
    Math.round(channel(h) * 255),
    Math.round(channel(h - 1 / 3) * 255),
  ];
}

/**
 * Shift lightness by `delta` (−1..1), clamped.
 *
 * Clamping matters at the ends of the range: Mina Prime's navy (#012943) is
 * already at 13% lightness, so a naive −0.10 would go negative and produce
 * black for the "dark" variant — visibly wrong, and exactly the kind of thing
 * that only shows up on one tenant's palette.
 */
export function shiftLightness(
  rgb: [number, number, number],
  delta: number,
): [number, number, number] {
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const next = Math.min(0.97, Math.max(0.03, l + delta));
  return hslToRgb(h, s, next);
}

/** Desaturate towards grey, for the accent's "muted" variant. */
export function desaturate(
  rgb: [number, number, number],
  amount: number,
): [number, number, number] {
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  return hslToRgb(h, Math.max(0, s * (1 - amount)), l);
}

const triple = (rgb: [number, number, number]): string => rgb.join(" ");

/**
 * The full CSS variable set for a brand, derived from its two colours.
 *
 * Returns an empty object for unparseable input rather than guessing, so the
 * CSS defaults survive. A tenant with a malformed colour renders in the
 * default brand — visibly wrong to an operator, but never unreadable to a user.
 */
export function brandCssVariables(
  primaryHex: string,
  accentHex: string,
): Record<string, string> {
  const primary = hexToRgb(primaryHex);
  const accent = hexToRgb(accentHex);
  const out: Record<string, string> = {};

  if (primary) {
    out["--brand-primary-rgb"] = triple(primary);
    out["--brand-primary-light-rgb"] = triple(shiftLightness(primary, 0.06));
    out["--brand-primary-dark-rgb"] = triple(shiftLightness(primary, -0.05));
    // Kept for anything still reading the original hex-valued variables.
    out["--brand-primary"] = primaryHex;
  }
  if (accent) {
    out["--brand-accent-rgb"] = triple(accent);
    out["--brand-accent-light-rgb"] = triple(shiftLightness(accent, 0.12));
    out["--brand-accent-dark-rgb"] = triple(shiftLightness(accent, -0.14));
    out["--brand-accent-muted-rgb"] = triple(desaturate(accent, 0.35));
    out["--brand-accent"] = accentHex;
  }
  return out;
}
